import { useCallback, useMemo, ComponentProps, memo } from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';

import { AppSwitcher } from '@atlaskit/atlassian-navigation';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { Placement } from '@atlassian/post-office-placement-remote';
import { RemoteComponent } from '@atlassian/remote-module-loader/remote-component';
import Switcher, {
  AtlassianSwitcherPrefetchTrigger,
} from '@atlassian/switcher';

import { useRemoteSwitcherPlugin } from '../../utils/use-remote-switcher-plugin';

import messages from './atlassian-switcher.i18n';
import { SwitcherContainer } from './atlassian-switcher.style';

export type AtlassianSwitcherProps = {
  globalItemStyles?: (styles: {}) => {};
  isAtlassianSwitcherOpen: boolean;
  isLoggedIn: boolean;
  onToggleAtlassianSwitcher: (isOpen: boolean) => void;
};

type InjectedAtlassianSwitcherProps = AtlassianSwitcherProps & {
  intl: InjectedIntl;
};

type BaseSwitcherProps = {
  appearance: 'standalone' | 'drawer';
  tenantId?: string;
};

type SwitcherComponentProps = ComponentProps<typeof Switcher>;

type DiscoveryExposureAttributes = {
  locale: string | undefined;
  ffEnableOverFiftyUsers: boolean | undefined;
  ffAllLocales: boolean | undefined;
};

type SwitcherDiscoveryExposureEvent = {
  actionSubject: 'section';
  action: 'viewed';
  actionSubjectId: 'switcherDiscoveryExposure';
  source: 'atlassianSwitcher';
  attributes: {
    locale: DiscoveryExposureAttributes['locale'];
    featureFlags: {
      ffEnableOverFiftyUsers: DiscoveryExposureAttributes['ffEnableOverFiftyUsers'];
      ffAllLocales: DiscoveryExposureAttributes['ffAllLocales'];
    };
  };
};

export const createSwitcherDiscoveryExposureEvent = ({
  locale,
  ffEnableOverFiftyUsers,
  ffAllLocales,
}: DiscoveryExposureAttributes): SwitcherDiscoveryExposureEvent => ({
  actionSubject: 'section',
  action: 'viewed',
  actionSubjectId: 'switcherDiscoveryExposure',
  source: 'atlassianSwitcher',
  attributes: {
    locale,
    featureFlags: {
      ffEnableOverFiftyUsers,
      ffAllLocales,
    },
  },
});

export const BaseSwitcher = memo((props: BaseSwitcherProps) => {
  const { appearance } = props;
  const remoteSwitcherPlugin = useRemoteSwitcherPlugin();

  const isPostOfficeIntegrationEnabled = FeatureGates.checkGate(
    StatsigFeatureKeys.postOfficeIntegrationInAppSwitcher
  );

  const switcherProps = useMemo<SwitcherComponentProps>(
    () => ({
      appearance,
      product: 'bitbucket',
      disableCustomLinks: true,
      discoverySection: isPostOfficeIntegrationEnabled ? (
        <Placement placementId="app-switcher-discovery-section" />
      ) : undefined,
    }),
    [appearance, isPostOfficeIntegrationEnabled]
  );

  return (
    <SwitcherContainer data-testid="base-switcher">
      <RemoteComponent<SwitcherComponentProps>
        remotePlugin={remoteSwitcherPlugin}
        analyticsChannel="navigation"
        ErrorFallback={<Switcher {...switcherProps} />}
        {...switcherProps}
      />
    </SwitcherContainer>
  );
});

export const SwitcherButton = injectIntl(
  ({
    onToggleAtlassianSwitcher,
    isSelected,
    intl,
    onMouseEnter,
  }: Omit<
    InjectedAtlassianSwitcherProps,
    'isAtlassianSwitcherOpen' | 'isLoggedIn'
  > & { isSelected?: boolean; onMouseEnter?: () => void }) => {
    const traceInteraction = usePressTracing('atlassian-switcher-open');

    const onClick = useCallback(() => {
      traceInteraction();
      onToggleAtlassianSwitcher(true);
    }, [onToggleAtlassianSwitcher, traceInteraction]);

    return (
      <AtlassianSwitcherPrefetchTrigger product="bitbucket">
        <AppSwitcher
          id="appSwitcher"
          testId="app-switcher"
          isSelected={isSelected}
          onClick={onClick}
          tooltip={intl.formatMessage(messages.tooltip)}
          onMouseEnter={onMouseEnter}
        />
      </AtlassianSwitcherPrefetchTrigger>
    );
  }
);
